import { Observable, defer, from, mergeMap, toArray, catchError, exhaustMap, filter, map, of, take, timeout, timer } from 'rxjs';

export interface ProjectionVersion {
  id?: number;
  concurrencyStamp?: string;
}

export interface ProjectionConsistencyConfig {
  loadById: (id: number) => Observable<ProjectionVersion | undefined>;
  loadByIds?: (ids: number[]) => Observable<ProjectionVersion[]>;
  polling?: ProjectionPollingOptions;
  onTimeout?: () => void;
}

export interface ProjectionPollingOptions {
  intervalMs?: number;
  timeoutMs?: number;
}

const DEFAULT_POLLING_INTERVAL_MS = 250;
const DEFAULT_POLLING_TIMEOUT_MS = 7_000;

export function waitForProjectedVersion<
  TSaved extends ProjectionVersion,
  TProjected extends ProjectionVersion,
>(
  saved: TSaved,
  loadById: (id: number) => Observable<TProjected | undefined>,
  polling: ProjectionPollingOptions = {},
): Observable<boolean> {
  if (saved.id === undefined) {
    return of(false);
  }

  const savedId = saved.id;
  return waitForProjectionCondition(
    () => loadById(savedId).pipe(map(projected =>
      projected !== undefined &&
      (saved.concurrencyStamp === undefined || projected.concurrencyStamp === saved.concurrencyStamp),
    )),
    polling,
  );
}

/** Polls the selected IDs independently of the grid filters and pagination. */
export function waitForProjectedDeletion(
  ids: readonly number[],
  consistency: ProjectionConsistencyConfig,
): Observable<boolean> {
  const selectedIds = [...new Set(ids)];
  if (selectedIds.length === 0) return of(true);

  const batches: number[][] = [];
  const batchSize = consistency.loadByIds ? 100 : 1;
  for (let offset = 0; offset < selectedIds.length; offset += batchSize) {
    batches.push(selectedIds.slice(offset, offset + batchSize));
  }

  return waitForProjectionCondition(() => from(batches).pipe(
    mergeMap(batch => checkProjectedBatchDeleted(batch, consistency), 4),
    toArray(),
    map(results => results.length === batches.length && results.every(Boolean)),
  ), consistency.polling);
}

function checkProjectedBatchDeleted(
  batch: number[],
  consistency: ProjectionConsistencyConfig,
): Observable<boolean> {
  return defer(() => consistency.loadByIds
    ? consistency.loadByIds(batch).pipe(map(rows => {
        if (!Array.isArray(rows)) throw new Error('Invalid projection response');
        return rows.length === 0;
      }))
    : consistency.loadById(batch[0]).pipe(map(row => row === undefined)),
  ).pipe(take(1));
}

/** A single deadline covers all reads in each non-overlapping polling cycle. */
export function waitForProjectionCondition(
  check: () => Observable<boolean>,
  polling: ProjectionPollingOptions = {},
): Observable<boolean> {
  const intervalMs = polling.intervalMs ?? DEFAULT_POLLING_INTERVAL_MS;
  const timeoutMs = polling.timeoutMs ?? DEFAULT_POLLING_TIMEOUT_MS;

  return timer(0, intervalMs).pipe(
    exhaustMap(() => defer(check).pipe(catchError(() => of(false)))),
    filter(consistent => consistent),
    take(1),
    timeout({ first: timeoutMs, with: () => of(false) }),
  );
}
